<template>
  <div class="experiments-page" :class="{ 'filters-open': filtersOpen }">
    <ExperimentFilterPanel
      id="experiment-filters"
      v-model="filters"
      class="filters-column"
      :options="filterOptions"
      :projects="projects"
      :loading="loading"
      @apply="applyFilters"
      @clear="clearFilters"
    />

    <section class="results-column" aria-label="Experiments">
      <div class="header">
        <h3>Experiments</h3>
        <div class="button-group">
          <button
            type="button"
            class="filters-toggle"
            :aria-expanded="filtersOpen ? 'true' : 'false'"
            aria-controls="experiment-filters"
            @click="filtersOpen = !filtersOpen"
          >
            {{ filtersOpen ? 'Hide filters' : 'Filters' }}
          </button>
          <button type="button" @click="navigateToCreateExperiment" class="new-experiment-button">
            New Experiment
          </button>
          <button type="button" @click="navigateToCreateSeries" class="new-experiment-button">
            Create Series
          </button>
        </div>
      </div>

      <div class="search-row">
        <label class="visually-hidden" for="experiment-search">Search experiments on this page</label>
        <input
          id="experiment-search"
          type="text"
          v-model="searchQuery"
          placeholder="Search"
          class="search-input"
        />
      </div>

      <ul v-if="activeChips.length" class="chips" aria-label="Active filters">
        <li v-for="chip in activeChips" :key="chip.key" class="chip">
          <span>{{ chip.label }}</span>
          <button type="button" :aria-label="`Remove filter ${chip.label}`" @click="removeChip(chip)">
            &times;
          </button>
        </li>
      </ul>

      <p class="result-count" aria-live="polite">{{ resultSummary }}</p>

      <div v-if="error" class="state-message error" role="alert">
        <span>{{ error }}</span>
        <button type="button" @click="fetchExperiments">Retry</button>
      </div>

      <div class="table-wrapper" :class="{ loading }" :aria-busy="loading ? 'true' : 'false'">
        <table class="experiment-table">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="`col-${column.key}`"
                :aria-sort="ariaSort(column)"
              >
                <button
                  v-if="column.sortBy"
                  type="button"
                  class="sort-button"
                  @click="setSort(column.sortBy)"
                >
                  {{ column.label }}
                  <i v-if="sort.by === column.sortBy" :class="arrowClass(sort.order)" aria-hidden="true"></i>
                </button>
                <span v-else>{{ column.label }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="experiment in visibleExperiments" :key="experiment.id" class="experiment-row">
              <td class="col-name ellipsis" :title="experiment.name">
                <button type="button" class="cell-link" @click="navigateToExperimentDetail(experiment.id)">
                  {{ experiment.name }}
                </button>
              </td>
              <td class="col-location ellipsis" :title="getLocationName(experiment.location_id)">
                <button type="button" class="cell-link" @click="navigateToLocationDetail(experiment.location_id)">
                  {{ getLocationName(experiment.location_id) }}
                </button>
              </td>
              <td class="col-start_date ellipsis">{{ formatDate(experiment.start_date) }}</td>
              <td class="col-end_date ellipsis">{{ formatDate(experiment.end_date) }}</td>
              <td class="col-project ellipsis" :title="getProjectName(experiment.project_id)">
                <button type="button" class="cell-link" @click="navigateToProjectDetail(experiment.project_id)">
                  {{ getProjectName(experiment.project_id) }}
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !error && !visibleExperiments.length">
              <td colspan="5" class="empty-state">
                No experiments match these filters.
                <button v-if="activeChips.length" type="button" class="cell-link" @click="clearFilters">
                  Clear filters
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav v-if="totalPages > 1" class="pagination" aria-label="Experiment pages">
        <button type="button" :disabled="currentPage === 1 || loading" @click="goToPage(currentPage - 1)">
          Previous
        </button>
        <button
          v-for="page in visiblePageNumbers"
          :key="page"
          type="button"
          :class="{ active: page === currentPage }"
          :aria-current="page === currentPage ? 'page' : null"
          :disabled="loading"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
        <button type="button" :disabled="currentPage >= totalPages || loading" @click="goToPage(currentPage + 1)">
          Next
        </button>
      </nav>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config.js';
import ExperimentFilterPanel from '@/components/ExperimentFilterPanel.vue';
import {
  OPERATOR_LABELS,
  buildFilterPayload,
  cloneFilters,
  emptyFilters,
  validateMetadataRow,
} from '@/utils/experimentFilters.js';

const PAGE_SIZE = 25;
const PAGE_WINDOW = 7;

export default {
  components: { ExperimentFilterPanel },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      experiments: [],
      projects: [],
      filterOptions: {},
      filters: emptyFilters(),
      appliedFilters: emptyFilters(),
      searchQuery: '',
      filtersOpen: false,
      loading: false,
      error: '',
      offset: 0,
      limit: PAGE_SIZE,
      total: 0,
      sort: { by: 'start_date', order: 'desc' },
      requestSeq: 0,
      columns: [
        { key: 'name', label: 'Name', sortBy: 'name' },
        { key: 'location', label: 'Location', sortBy: null },
        { key: 'start_date', label: 'Start Date', sortBy: 'start_date' },
        { key: 'end_date', label: 'End Date', sortBy: 'end_date' },
        { key: 'project', label: 'Project', sortBy: null },
      ],
    };
  },
  mounted() {
    this.fetchFilterOptions();
    this.fetchProjects();
    this.fetchExperiments();
  },
  computed: {
    ...mapState(['loggedInUser']),
    locations() {
      return this.filterOptions.locations || [];
    },
    visibleExperiments() {
      const q = this.searchQuery.toLowerCase().trim();
      if (!q) return this.experiments;
      return this.experiments.filter((exp) => (exp?.name ?? '').toLowerCase().includes(q));
    },
    resultSummary() {
      if (this.loading) return 'Loading…';
      if (this.searchQuery.trim()) {
        return `${this.visibleExperiments.length} of ${this.experiments.length} on this page match "${this.searchQuery.trim()}" (${this.total} total)`;
      }
      return `${this.total} experiment${this.total === 1 ? '' : 's'}`;
    },
    currentPage() {
      return Math.floor(this.offset / this.limit) + 1;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
    visiblePageNumbers() {
      let start = Math.max(1, this.currentPage - Math.floor(PAGE_WINDOW / 2));
      const end = Math.min(this.totalPages, start + PAGE_WINDOW - 1);
      start = Math.max(1, end - PAGE_WINDOW + 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    activeChips() {
      const f = this.appliedFilters;
      const chips = [];
      const add = (group, items, prefix, label) =>
        items.forEach((item) =>
          chips.push({ key: `${group}-${item.id}`, group, id: item.id, label: `${prefix}: ${label(item)}` }),
        );

      add('projects', f.projects, 'Project', (i) => i.name);
      add('locations', f.locations, 'Location', (i) => i.name);
      add('instruments', f.instruments, 'Instrument', (i) => i.name);
      add('materials', f.materials, 'Material', (i) => i.name);
      add('relationTypes', f.relationTypes, 'Relation', (i) => i.name);

      if (f.startDate || f.endDate) {
        chips.push({
          key: 'date',
          group: 'date',
          label: `Date: ${f.startDate || '…'} – ${f.endDate || '…'}`,
        });
      }

      f.metadata.forEach((row) => {
        const result = validateMetadataRow(row);
        if (result.status !== 'ok') return;
        const { value, value_to: valueTo } = result.filter;
        const valueText = valueTo !== undefined ? `${value} – ${valueTo}` : (value ?? '');
        chips.push({
          key: row.uid,
          group: 'metadata',
          id: row.uid,
          label: `${row.key.name} ${OPERATOR_LABELS[row.operator] || row.operator} ${valueText}`.trim(),
        });
      });

      return chips;
    },
  },
  methods: {
    async fetchFilterOptions() {
      try {
        const response = await axios.get(API_ENDPOINTS.experimentFilterOptions, {
          params: { entra_id: this.loggedInUser.entra_id },
        });
        this.filterOptions = response.data || {};
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
        this.toast.error('Failed to load filter options.');
      }
    },
    async fetchProjects() {
      try {
        const response = await axios.get(API_ENDPOINTS.projects, {
          params: { entra_id: this.loggedInUser.entra_id },
        });
        this.projects = response.data;
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    },
    async fetchExperiments() {
      const requestId = ++this.requestSeq;
      this.loading = true;
      this.error = '';

      try {
        const response = await axios.post(API_ENDPOINTS.filteredExperiments, {
          entra_id: this.loggedInUser.entra_id,
          filters: buildFilterPayload(this.appliedFilters),
          pagination: { offset: this.offset, limit: this.limit },
          sort: { by: this.sort.by, order: this.sort.order },
        });
        if (requestId !== this.requestSeq) return;

        const items = response.data?.items ?? [];
        this.experiments = items;
        this.total = response.data?.pagination?.total ?? items.length;
      } catch (error) {
        if (requestId !== this.requestSeq) return;
        console.error('Error fetching experiments:', error);
        this.error = 'Failed to fetch experiments.';
        this.experiments = [];
        this.total = 0;
      } finally {
        if (requestId === this.requestSeq) this.loading = false;
      }
    },
    applyFilters() {
      this.appliedFilters = cloneFilters(this.filters);
      this.offset = 0;
      this.filtersOpen = false;
      this.fetchExperiments();
    },
    clearFilters() {
      this.filters = emptyFilters();
      this.appliedFilters = emptyFilters();
      this.offset = 0;
      this.fetchExperiments();
    },
    removeChip(chip) {
      const strip = (f) => {
        if (chip.group === 'date') {
          f.startDate = '';
          f.endDate = '';
        } else if (chip.group === 'metadata') {
          f.metadata = f.metadata.filter((row) => row.uid !== chip.id);
        } else {
          f[chip.group] = f[chip.group].filter((item) => item.id !== chip.id);
          if (chip.group === 'materials' && !f.materials.length) f.relationTypes = [];
        }
      };
      strip(this.filters);
      strip(this.appliedFilters);
      this.offset = 0;
      this.fetchExperiments();
    },
    setSort(by) {
      if (this.sort.by === by) {
        this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = { by, order: 'asc' };
      }
      this.offset = 0;
      this.fetchExperiments();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.offset = (page - 1) * this.limit;
      this.fetchExperiments();
    },
    ariaSort(column) {
      if (!column.sortBy) return null;
      if (this.sort.by !== column.sortBy) return 'none';
      return this.sort.order === 'asc' ? 'ascending' : 'descending';
    },
    arrowClass(order) {
      return order === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
    },
    getProjectName(projectId) {
      const project = this.projects.find((p) => +p.id === +projectId);
      return project ? project.name : 'Unknown Project';
    },
    getLocationName(locationId) {
      const location = this.locations.find((l) => +l.id === +locationId);
      return location ? location.name : 'Unknown Location';
    },
    navigateToCreateExperiment() {
      this.$router.push({ path: '/create-experiment' });
    },
    navigateToCreateSeries() {
      this.$router.push({
        path: '/create-series',
        query: {
          returnTo: this.$route.fullPath,
          returnToName: this.$route.name,
          returnToParams: JSON.stringify(this.$route.params || {}),
          returnToQuery: JSON.stringify(this.$route.query || {}),
        },
      });
    },
    navigateToExperimentDetail(experimentId) {
      this.$router.push({ name: 'ExperimentDetailPage', params: { id: experimentId } });
    },
    navigateToProjectDetail(projectId) {
      this.$router.push({ name: 'ProjectDetailPage', params: { id: projectId } });
    },
    navigateToLocationDetail(locationId) {
      this.$router.push({ name: 'LocationDetailPage', params: { id: locationId } });
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.experiments-page {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  margin: 20px auto;
  padding: 0 20px;
  max-width: 1600px;
}

.filters-column {
  // No overflow/max-height here: it would clip the open multiselect dropdowns
  position: relative;
  z-index: 20;
}

.results-column {
  min-width: 0;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .button-group {
    display: flex;
    gap: 10px;
  }
}

.new-experiment-button,
.filters-toggle {
  padding: 8px 16px;
  background-color: $primary-color;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

.filters-toggle {
  display: none;
}

.search-row {
  margin-bottom: 8px;
}

.search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid #c8c8c0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 8px 0;
  padding: 0;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px 2px 10px;
  background: $third-color;
  border-radius: 999px;
  font-size: 0.85rem;

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1.1rem;
    line-height: 1;
    padding: 0 6px;
    color: $primary-color;
  }
}

.result-count {
  margin: 8px 0;
  font-size: 0.9rem;
  color: #444;
}

.state-message {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 5px;

  &.error {
    background: #fdecea;
    color: #b3261e;
  }

  button {
    padding: 4px 10px;
    cursor: pointer;
  }
}

.table-wrapper {
  overflow-x: auto;
  transition: opacity 0.15s;

  &.loading {
    opacity: 0.5;
  }
}

.experiment-table {
  table-layout: fixed;
  // Override the global centered 80% table rule in main.scss
  width: 100%;
  max-width: none;
  margin: 0;
  min-width: 600px;

  th {
    background-color: $form-color;
    border: 1px solid $primary-color;
    text-align: left;
  }

  th,
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 8px;
  }

  .experiment-row {
    background-color: $secondary-color;

    &:hover {
      background-color: $form-color;
    }
  }

  .col-name { width: 25%; }
  .col-location { width: 20%; }
  .col-start_date,
  .col-end_date { width: 15%; }
  .col-project { width: 25%; }
}

.sort-button,
.cell-link {
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 2px;
  }
}

.sort-button {
  width: 100%;
  font-weight: bold;
  color: $primary-color;
}

.cell-link {
  color: $primary-color;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-state {
  text-align: center;
  white-space: normal;
  padding: 24px 8px;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;

  button {
    padding: 5px 10px;
    background-color: $primary-color;
    color: #fff;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;

    &:hover:not(:disabled),
    &.active {
      background-color: darken($primary-color, 10%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.active {
      outline: 2px solid $third-color;
    }
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .experiments-page {
    grid-template-columns: minmax(0, 1fr);
  }

  .filters-column {
    display: none;
  }

  .filters-open .filters-column {
    display: block;
  }

  .filters-toggle {
    display: inline-block;
  }
}
</style>
